import { useCallback, useState } from 'react'
import { JoinRequest } from '@weblite/ts-action/hall'
import { CheckIcon, ClearIcon } from '@weblite/icons'
import {
  Divider,
  Typography,
  styled,
  ButtonBase,
  CircularProgress,
} from '@mui/material'
import { openUserProfile } from '$logic/router'
import { getUserDisplayName, useUser } from '$logic/users'
import { AvatarSize, UserAvatar } from '$common/avatar'
import { isRecentDate } from '$func/date'
import { t } from '$i18n'
import { formatClock, formatDate, shortDateFormatter } from '$func/time'
import { JoinHallRequestListItemSkeleton } from './join-hall-request-list-item-skeleton'
import { handleJoinHallRequests } from '$logic/subscription'
import {
  showFailedToAcceptJoinRequestSnackbar,
  showFailedToRejectJoinRequestSnackbar,
  showJoinRequestAcceptedSnackbar,
  showJoinRequestRejectedSnackbar,
} from '$comp/snackbars'
import { removeFromOpenedUserList } from '$logic/opened-user-list/service'
import { JOIN_HALL_REQUESTS_STORAGE_KEY } from '$logic/opened-user-list'
import { closeCurrentModal } from 'weblite-web-modal-viewer'

export const JOIN_CHANNEL_REQUEST_LIST_ITEM_HEIGHT = 72

interface Props extends JoinRequest {
  roomId: string
}

export function JoinHallRequestListItem({
  userId,
  requestedAt,
  link,
  roomId,
}: Props) {
  const { user } = useUser(userId)

  const userName = getUserDisplayName(user)

  const [state, setState] = useState<'accepting' | 'rejecting' | 'idle'>('idle')

  const handleOpenProfile = useCallback(() => {
    void openUserProfile(userId)
  }, [userId])

  const handleAccept = useCallback(async () => {
    setState('accepting')
    try {
      const { success, showSnackbar } = await handleJoinHallRequests(
        roomId,
        [
          {
            link,
            userId,
            approved: true,
          },
        ],
        closeCurrentModal,
      )
      if (success) {
        removeFromOpenedUserList(userId, JOIN_HALL_REQUESTS_STORAGE_KEY)
        showJoinRequestAcceptedSnackbar(userName)
      } else if (showSnackbar) showFailedToAcceptJoinRequestSnackbar(userName)
    } catch (error) {
      showFailedToAcceptJoinRequestSnackbar(userName)
    } finally {
      setState('idle')
    }
  }, [link, userId, roomId, userName])

  const handleReject = useCallback(async () => {
    setState('rejecting')
    try {
      const { success, showSnackbar } = await handleJoinHallRequests(roomId, [
        {
          link,
          userId,
          approved: false,
        },
      ])
      if (success) {
        removeFromOpenedUserList(userId, JOIN_HALL_REQUESTS_STORAGE_KEY)
        showJoinRequestRejectedSnackbar(userName)
      } else if (showSnackbar) showFailedToRejectJoinRequestSnackbar(userName)
    } catch (error) {
      showFailedToRejectJoinRequestSnackbar(userName)
    } finally {
      setState('idle')
    }
  }, [link, userId, roomId, userName])

  if (!user) return <JoinHallRequestListItemSkeleton />

  return (
    <>
      <ListItemRoot>
        <ListItemBackgroundButton onClick={handleOpenProfile} />
        <ListItemContent>
          <ListItemUserInfo>
            <UserAvatar
              onClick={handleOpenProfile}
              sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
              userId={userId}
              size={AvatarSize.MD}
            />
            <ListItemTexts>
              <Typography
                onClick={handleOpenProfile}
                sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
                variant="subtitle3"
                color="neutral1000"
              >
                {getUserDisplayName(user)}
              </Typography>
              <Typography variant="caption1" color="neutral500">
                {requestedToJoinAt(requestedAt)}
              </Typography>
            </ListItemTexts>
          </ListItemUserInfo>
          <ListItemActions>
            <RejectRequestButton
              onClick={handleReject}
              disabled={state !== 'idle'}
            >
              {state === 'rejecting' ? (
                <CircularProgress size={24} />
              ) : (
                <ClearIcon sx={{ width: '24px', height: '24px' }} />
              )}
            </RejectRequestButton>
            <AcceptRequestButton
              onClick={handleAccept}
              disabled={state !== 'idle'}
            >
              {state === 'accepting' ? (
                <CircularProgress size={24} />
              ) : (
                <CheckIcon sx={{ width: '24px', height: '24px' }} />
              )}
            </AcceptRequestButton>
          </ListItemActions>
        </ListItemContent>
      </ListItemRoot>
      <Divider variant="inset" />
    </>
  )
}

const ListItemRoot = styled('div')`
  height: ${JOIN_CHANNEL_REQUEST_LIST_ITEM_HEIGHT}px;
  position: relative;
  width: 100%;
`

const ListItemBackgroundButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`

const ListItemContent = styled('div')`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;
`

const ListItemUserInfo = styled('div')`
  gap: 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const ListItemTexts = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`

const ListItemActions = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: auto;
`

const AcceptRequestButton = styled(ButtonBase)`
  background-color: ${({ theme }) => theme.palette.primary500};
  color: ${({ theme }) => theme.palette.neutral000};
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RejectRequestButton = styled(ButtonBase)`
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.palette.error300};
  color: ${({ theme }) => theme.palette.error300};
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`

function requestedToJoinAt(time: number | string) {
  const date = new Date(time)

  const { isToday, isYesterday, isThisYear } = isRecentDate(date)

  const joinedAt = t('JoinHallRequests.list.requestedToJoinAt')

  return isToday
    ? `${joinedAt} ${formatClock(date)}`
    : isYesterday
      ? `${joinedAt} ${t('common.yesterday')}`
      : isThisYear
        ? `${joinedAt} ${shortDateFormatter.format(date)}`
        : `${joinedAt} ${formatDate(date)}`
}
